import { useBoardPermissionsCache } from '~/hooks/useBoardPermissionsCache';
import { useFetchObjectsPermissions } from '~/hooks/useFetchObjectsPermissions';
import { useAccountContext } from '~/providers/AccountProvider';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { type CanvasListItem } from '~/swr-hooks/canvases/types';
import { canEditCanvasInBoard } from '~/utils/permissions/boardPermissions';
import { canEditCanvasInWorkspace } from '~/utils/permissions/workspacePermissions';

export const useCanEditCanvasListItem = (canvas: CanvasListItem) => {
  const { getBoardPermissions } = useBoardPermissionsCache();
  const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();
  const { data: account } = useAccountContext();

  // Ensure board permissions are fetched so the hook self-heals when called
  // outside of CanvasesGalleryView (e.g. sidebar), where no bulk fetch exists.
  const boardId = canvas.boardId;
  useFetchObjectsPermissions({
    objects: { boardIds: boardId ? [boardId] : [] },
    options: { enabled: !!boardId },
  });

  if (!account) {
    return { canEdit: false, isLoading: true };
  }

  const isOwner = !!account.id && canvas.createdBy === account.id;

  if (boardId) {
    const boardPerms = getBoardPermissions(boardId);

    if (!boardPerms) {
      return { canEdit: false, isLoading: true };
    }

    return { canEdit: isOwner && canEditCanvasInBoard(boardPerms), isLoading: false };
  }

  return { canEdit: isOwner && canEditCanvasInWorkspace(workspacePermissions), isLoading: false };
};
